// Bambody v3 PDP variant picker + ATC.
// Simpler than bambody-v3-shop.js — bound to a single product. Reads product
// data from <script data-bam-v3-pdp-data>{...}</script>, drives color/size/pack
// selection, image swap on color hover, ATC via /cart/add.js, gallery arrows.
// Fires bam:cart:added so the drawer opens (same event bambody-v3-shop.js uses).

(function () {
  const COLOR_MAP = {
    Black: '#1F0E33', Nude: '#E5C6A9', Gray: '#9A9AA0',
    'Dark Blue': '#2A3A6B', 'Dark Purple': '#4B2C7A',
    Pink: '#F5B6C7', Blue: '#8DC3E1', Green: '#B7DAB9', Lace: '#1F0E33',
  }

  function colorSlug(raw) {
    return String(raw || '').toLowerCase()
      .replace(/\s*—\s*/g, '-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }
  function stripFlow(raw) {
    return String(raw || '').replace(/^(Heavy|Light)(?:\s+Flow)?\s*—\s*/, '').trim()
  }
  function fmtPrice(cents) {
    return '$' + ((Number(cents) || 0) / 100).toFixed(2)
  }
  function shortSize(s) {
    return String(s || '')
      .replace('XX-Small', 'XXS').replace('X-Small', 'XS').replace('Small', 'S')
      .replace('Medium', 'M')
      .replace('XX-Large', 'XXL').replace('3X-Large', '3X').replace('4X-Large', '4X').replace('5X-Large', '5X').replace('6X-Large', '6X').replace('X-Large', 'XL')
      .replace('Large', 'L')
  }

  // CSS background for a colour option: solid, or pie slices for a multi-colour pack.
  function swatchBg(colorRaw) {
    const parts = stripFlow(colorRaw).replace(/\s+with\s+lace/i, '').split(/\s*\/\s*/).map((s) => s.trim()).filter(Boolean)
    if (parts.length === 1) return COLOR_MAP[parts[0]] || '#ccc'
    const step = 360 / parts.length
    const stops = parts.map((c, i) => `${COLOR_MAP[c] || '#ccc'} ${i * step}deg ${(i + 1) * step}deg`).join(', ')
    return `conic-gradient(from -90deg, ${stops})`
  }

  function makeSwatchEl(colorRaw, oos) {
    const el = document.createElement('button')
    el.type = 'button'
    el.className = 'sw' + (oos ? ' oos' : '')
    el.style.background = swatchBg(colorRaw)
    return el
  }

  let PDP = null
  // note: the sentence explaining an option we changed for the shopper on the
  // last pick (e.g. a size that only comes in another colour), shown once.
  const S = { color: null, size: null, pack: null, imageIndex: null, note: '' }

  function loadData() {
    const el = document.querySelector('script[data-bam-v3-pdp-data]')
    if (!el) return null
    try { return JSON.parse(el.textContent) } catch { return null }
  }

  function variantOf(sel) {
    return PDP.variants.find((v) => v.color === sel.color && v.size === sel.size && v.pack === sel.pack) || null
  }
  function currentVariant() {
    if (!PDP) return null
    return variantOf(S)
  }
  const inStock = (pred) => PDP.variants.some((v) => v.available && pred(v))
  const colours = () => [...new Set(PDP.variants.map((v) => v.color))]

  // Canonical Bambody size order (XXS → 6XL). Any label not in the map sorts to the end.
  const SIZE_ORDER = ['XX-Small','X-Small','Small','Medium','Large','X-Large','XX-Large','3X-Large','4X-Large','5X-Large','6X-Large']
  const sizeRank = (s) => { const i = SIZE_ORDER.indexOf(s); return i < 0 ? 999 : i }
  // Pack order: single pair → larger bundles, consistent across products.
  const PACK_ORDER = ['1pk','2pk','3pk','4pk','5pk','6pk']
  const packRank = (s) => { const i = PACK_ORDER.indexOf(s); return i < 0 ? 999 : i }

  // One availability model for every picker (pills, sticky popovers, the phone
  // dropdowns that mirror the pills):
  //   colour  in stock at all;
  //   size    in stock in the chosen colour; if not, `others` lists the colours
  //           that do have it in stock (empty = sold out everywhere);
  //   pack    in stock for the chosen colour + size.
  function allValues(axis) {
    const seen = []
    for (const v of PDP.variants) {
      if (v[axis] && !seen.some((x) => x.value === v[axis])) seen.push({ value: v[axis] })
    }
    for (const s of seen) {
      if (axis === 'color') s.available = inStock((v) => v.color === s.value)
      else if (axis === 'size') {
        s.available = inStock((v) => v.size === s.value && v.color === S.color)
        s.others = s.available ? [] : colours().filter((c) => inStock((v) => v.size === s.value && v.color === c))
      } else s.available = inStock((v) => v.pack === s.value && v.color === S.color && v.size === S.size)
    }
    if (axis === 'size') seen.sort((a, b) => sizeRank(a.value) - sizeRank(b.value))
    if (axis === 'pack') seen.sort((a, b) => packRank(a.value) - packRank(b.value))
    return seen
  }

  function selectDefaultAxes() {
    const first = PDP.variants.find((v) => v.id === PDP.defaultVariantId) || PDP.variants.find((v) => v.available) || PDP.variants[0]
    S.color = first.color
    S.size = first.size
    S.pack = first.pack
  }

  // In-stock pack for (color, size), keeping `prefer` when it is in stock.
  function packFor(color, size, prefer) {
    const packs = PDP.variants.filter((v) => v.color === color && v.size === size && v.available).map((v) => v.pack)
    packs.sort((a, b) => packRank(a) - packRank(b))
    return packs.includes(prefer) ? prefer : (packs[0] || null)
  }

  // What picking `axis` = `value` selects, plus the sentence explaining any other
  // option we had to change for it. Pure (S is not touched), so a colour hover can
  // ask what that colour would show without selecting it.
  //   colour: NEVER changes the size. A size we picked for the shopper is a
  //           wrong-size order waiting to happen, so if the colour isn't in
  //           stock in their size we keep it, block Add to cart, and the note
  //           says what is in stock (renderSizeNote);
  //   size:   keep the colour if it has the size in stock, else switch to a
  //           colour that does (one with the same pack first), and say so;
  //   then keep the pack if it is in stock, else the smallest in-stock pack.
  function resolve(axis, value) {
    const sel = { color: S.color, size: S.size, pack: S.pack }
    let note = ''
    sel[axis] = value
    if (axis === 'color' && !inStock((v) => v.color === value && v.size === sel.size)) {
      // Land on a pack this colour is made in, so the photo and pack row match it.
      const packs = PDP.variants.filter((v) => v.color === value).map((v) => v.pack)
      if (!packs.includes(sel.pack)) sel.pack = packs.sort((a, b) => packRank(a) - packRank(b))[0] || sel.pack
    } else if (axis === 'size' && !inStock((v) => v.color === sel.color && v.size === value)) {
      const cs = colours().filter((c) => inStock((v) => v.color === c && v.size === value))
      const c = cs.find((c) => inStock((v) => v.color === c && v.size === value && v.pack === sel.pack)) || cs[0]
      if (c) {
        note = `${shortSize(value)} is in stock in ${stripFlow(c)}, so we switched the colour.`
        sel.color = c
      } else if (!PDP.variants.some((v) => v.color === sel.color && v.size === value)) {
        sel.color = (PDP.variants.find((v) => v.size === value) || sel).color
      }
    }
    if (axis !== 'pack') sel.pack = packFor(sel.color, sel.size, sel.pack) || sel.pack
    // This colour + size is made but sold out in this pack: land on a real variant
    // in another pack so the buy box says "Sold out". If it isn't made at all,
    // the selection stays variant-less and renderBuy blocks Add to cart.
    if (!variantOf(sel)) {
      const v = PDP.variants.find((v) => v.color === sel.color && v.size === sel.size)
      if (v) sel.pack = v.pack
    }
    return { sel, note }
  }

  // "XS, S" · "3X–6X": three or more neighbours on this product's size ladder
  // collapse into a range.
  function sizeList(values, ladder) {
    const idx = values.map((v) => ladder.indexOf(v)).filter((i) => i >= 0).sort((a, b) => a - b)
    const out = []
    for (let i = 0; i < idx.length; i++) {
      let j = i
      while (j + 1 < idx.length && idx[j + 1] === idx[j] + 1) j++
      const a = shortSize(ladder[idx[i]]), b = shortSize(ladder[idx[j]])
      if (j - i >= 2) out.push(`${a}–${b}`)
      else for (let k = i; k <= j; k++) out.push(shortSize(ladder[idx[k]]))
      i = j
    }
    return out.join(', ')
  }

  // ── Renderers ──
  function renderSwatches() {
    const el = document.getElementById('bam-v3-pdp-swatch-row')
    if (!el) return
    el.innerHTML = ''
    for (const { value, available } of allValues('color')) {
      const sw = makeSwatchEl(value, !available)
      const name = stripFlow(value)
      // In stock, just not in the chosen size: slashed (the note explains once it is picked).
      const noSize = available && S.color !== value && S.size && !inStock((v) => v.color === value && v.size === S.size)
      if (noSize) sw.classList.add('no-size')
      sw.title = !available ? `${name}: sold out` : noSize ? `${name}: not in ${shortSize(S.size)}` : name
      sw.setAttribute('aria-label', sw.title)
      sw.setAttribute('aria-pressed', S.color === value ? 'true' : 'false')
      if (S.color === value) sw.classList.add('active')
      sw.onclick = () => bamV3PdpSelect('color', value)
      sw.addEventListener('mouseenter', () => previewImageForColor(value))
      sw.addEventListener('mouseleave', () => previewImageForColor(null))
      el.appendChild(sw)
    }
    const cur = document.getElementById('bam-v3-pdp-color-label')
    if (cur) cur.textContent = stripFlow(S.color || '')
  }

  // Size pills for the chosen colour. A size that is only in stock in other
  // colours stays tappable (.is-other, dashed, a dot per colour that has it) and
  // switches the colour; only a size sold out everywhere is greyed. data-hint
  // carries the same cue into the phone dropdown (bambody-v4-mobile-picker.js),
  // which copies each button's text, so the button text stays the bare size.
  function renderSizes() {
    const el = document.getElementById('bam-v3-pdp-size-row')
    if (!el) return
    el.innerHTML = allValues('size').map(({ value, available, others }) => {
      const other = !available && others.length > 0
      const cls = 'sz-btn' + (S.size === value ? ' active' : '') + (other ? ' is-other' : available ? '' : ' oos')
      const names = others.map(stripFlow).join(', ')
      const label = shortSize(value) + (other ? `, in stock in ${names}` : available ? '' : ', sold out')
      const hint = other ? (others.length === 1 ? `In ${stripFlow(others[0])}` : 'Other colours') : ''
      const dots = other ? `<span class="sz-dots" aria-hidden="true">${others.slice(0, 3).map((c) => `<i style="background:${swatchBg(c)}"></i>`).join('')}</span>` : ''
      return `<button type="button" class="${cls}" aria-label="${label}"${hint ? ` data-hint="${hint}"` : ''} onclick="bamV3PdpSelect('size','${value.replace(/'/g, "\\'")}')">${shortSize(value)}${dots}</button>`
    }).join('')
    renderSizeNote()
  }

  // One line under the sizes, in order of precedence:
  //   is-change  a size pick switched the colour: say so;
  //   is-warn    the chosen colour isn't in stock in the chosen size: say what is,
  //              and that tapping the size gets it in a colour that has it;
  //   info       name the sizes only in stock in other colours.
  // It lives OUTSIDE #bam-v3-pdp-size-row because phones hide that row and show
  // a dropdown instead.
  function renderSizeNote() {
    const row = document.getElementById('bam-v3-pdp-size-row')
    if (!row) return
    let n = document.getElementById('bam-v3-pdp-size-note')
    if (!n) {
      n = document.createElement('p')
      n.id = 'bam-v3-pdp-size-note'
      n.setAttribute('aria-live', 'polite')
      row.after(n)
    }
    const sizes = allValues('size')
    const ladder = sizes.map((s) => s.value)
    let text = S.note, kind = S.note ? 'change' : ''
    if (!text && !inStock((v) => v.color === S.color && v.size === S.size)) {
      const here = sizes.filter((s) => s.available).map((s) => s.value)
      const mine = sizes.find((s) => s.value === S.size)
      const colour = stripFlow(S.color)
      text = here.length
        ? `${colour} isn't in stock in ${shortSize(S.size)}. In stock: ${sizeList(here, ladder)}.`
        : `${colour} is sold out right now.`
      if (mine?.others.length) text += ` Tap ${shortSize(S.size)} to get it in ${mine.others.length === 1 ? stripFlow(mine.others[0]) : 'another colour'}.`
      kind = 'warn'
    }
    if (!text) {
      const other = sizes.filter((s) => !s.available && s.others.length)
      if (other.length) {
        const shared = colours().filter((c) => other.every((s) => s.others.includes(c)))
        const where = shared.length === 1 ? stripFlow(shared[0]) : 'other colours'
        text = `${sizeList(other.map((s) => s.value), ladder)} ${other.length === 1 ? 'is' : 'are'} in stock in ${where}. Pick one and we'll switch the colour.`
      }
    }
    n.className = 'bam-v3-pdp-size-note' + (kind ? ` is-${kind}` : '')
    n.textContent = text || ''
    n.hidden = !text
  }

  function renderPacks() {
    const el = document.getElementById('bam-v3-pdp-pack-row')
    if (!el) return
    el.innerHTML = allValues('pack').map(({ value, available }) => {
      const label = value === '1pk' ? 'Single pair' : value.replace(/pk$/, '-pack') + ' bundle'
      return `<button type="button" class="pack-btn${S.pack === value ? ' active' : ''}${available ? '' : ' oos'}" onclick="bamV3PdpSelect('pack','${value}')"><span class="pack-name">${label}</span><span class="pack-sub"></span></button>`
    }).join('')
  }

  function renderBuy() {
    const v = currentVariant()
    const priceMain = document.getElementById('bam-v3-pdp-price-main')
    const priceSub = document.getElementById('bam-v3-pdp-price-sub')
    const atc = document.getElementById('bam-v3-pdp-atc-btn')
    if (!v) {
      // Colour not made in this size (resolve keeps the shopper's size): the size note says what is.
      if (priceMain) priceMain.textContent = `Not in ${shortSize(S.size)}`
      if (priceSub) priceSub.textContent = ''
      if (atc) atc.disabled = true
      syncExpressForm(null)
      return
    }
    if (priceMain) priceMain.textContent = fmtPrice(v.price)
    const packLabel = v.pack === '1pk' ? 'single pair' : v.pack.replace(/pk$/, '-pack')
    if (priceSub) priceSub.textContent = packLabel
    if (atc) {
      atc.disabled = !v.available
      atc.dataset.variantId = v.id
    }
    syncExpressForm(v)
  }

  // Update the hidden variant id on the express-checkout form so payment_button submits the correct variant.
  // With no buyable variant the wallet buttons are hidden: their form would still
  // carry the previous variant's id and check out something the page no longer shows.
  function syncExpressForm(variant) {
    const input = document.querySelector('[data-express-id]')
    if (input && variant) input.value = variant.id
    const row = document.querySelector('#bam-v3-pdp .bam-v3-express-row')
    if (row) row.style.display = variant && variant.available ? '' : 'none'
  }

  // The photo for a selection {color, size, pack}, as {big, thumb}. Priority:
  //   1. that exact variant's own image (variant.featured_media, set in admin),
  //   2. any variant of the same (colour, pack) with an image: a pack photo is
  //      the same whatever the size,
  //   3. any variant of that colour with an image,
  //   4. the tagged heroes from the Amazon pipeline (bam-pack, then bam-color),
  //   5. the product default.
  function heroFor(sel) {
    const pick = (v) => v && v.image ? { big: v.image, thumb: v.thumb || v.image } : null
    const hit = pick(variantOf(sel))
      || pick(PDP.variants.find((v) => v.color === sel.color && v.pack === sel.pack && v.image))
      || pick(PDP.variants.find((v) => v.color === sel.color && v.image))
    if (hit) return hit
    const slug = colorSlug(sel.color)
    const tagged = (sel.pack && PDP.packImages?.[`${slug}:${sel.pack}`]) || PDP.images?.[slug]
    if (tagged) return { big: tagged, thumb: tagged }
    return PDP.defaultImage ? { big: PDP.defaultImage, thumb: PDP.defaultImage } : null
  }
  function mainImageFor() {
    return heroFor(S)?.big || null
  }

  function renderImage() {
    const el = document.getElementById('bam-v3-pdp-img')
    if (!el) return
    const media = currentMedia()
    let url = null
    if (S.imageIndex != null && media[S.imageIndex]) {
      url = media[S.imageIndex].big
    } else {
      url = mainImageFor() || media[0]?.big || null
    }
    if (url) {
      el.src = url
      el.alt = `${PDP.title} — ${stripFlow(S.color)}`
      el.removeAttribute('data-empty')
    } else {
      el.removeAttribute('src')
      el.alt = PDP.title
      el.setAttribute('data-empty', '')
    }
    // Arrows + counter — visible only if multiple images in the current-color filter.
    const prev = document.getElementById('bam-v3-pdp-prev')
    const next = document.getElementById('bam-v3-pdp-next')
    const counter = document.getElementById('bam-v3-pdp-img-count')
    const showControls = media.length > 1
    if (prev) prev.hidden = !showControls
    if (next) next.hidden = !showControls
    if (counter) {
      if (showControls) {
        const displayIdx = S.imageIndex != null
          ? Math.min(S.imageIndex, media.length - 1)
          : Math.max(0, media.findIndex((m) => m.big === url))
        counter.textContent = `${displayIdx + 1} / ${media.length}`
        counter.hidden = false
      } else counter.hidden = true
    }
  }

  // Hovering a swatch previews exactly what picking it would show (same size and
  // pack rules as a click, via resolve), photo and colour name. Leaving restores
  // the real selection, including a gallery photo the shopper had picked.
  function previewImageForColor(colorRaw) {
    const el = document.getElementById('bam-v3-pdp-img'); if (!el) return
    const label = document.getElementById('bam-v3-pdp-color-label')
    if (!colorRaw) {
      renderImage()
      if (label) label.textContent = stripFlow(S.color || '')
      return
    }
    const hero = heroFor(resolve('color', colorRaw).sel)
    if (hero) {
      el.src = hero.big
      el.alt = `${PDP.title} — ${stripFlow(colorRaw)}`
    }
    if (label) label.textContent = stripFlow(colorRaw)
  }

  // Media list powering the thumb strip AND arrow cycling.
  // Position 0 = the selected variant's photo. Positions 1..N = SHARED family media
  // (info cards, cert badges, back views), the same for every colour. PDP.mediaList
  // (from Liquid) already leaves out every variant-specific photo, so the gallery
  // never shows another colour or pack.
  function currentMedia() {
    const shared = Array.isArray(PDP?.mediaList) ? PDP.mediaList : []
    const hero = heroFor(S)
    if (!hero) return shared
    return [hero, ...shared.filter((m) => m.big !== hero.big)]
  }

  function renderThumbs() {
    const el = document.getElementById('bam-v3-pdp-thumbs')
    if (!el) return
    const media = currentMedia()
    if (media.length <= 1) { el.innerHTML = ''; return }
    const currentSrc = document.getElementById('bam-v3-pdp-img')?.getAttribute('src')
    const activeIdx = (S.imageIndex != null)
      ? Math.min(S.imageIndex, media.length - 1)
      : Math.max(0, media.findIndex((m) => m.big === currentSrc))
    el.innerHTML = media.map((m, i) => {
      const cls = 'bam-v3-pdp__thumb' + (i === activeIdx ? ' active' : '')
      return `<button type="button" class="${cls}" onclick="bamV3PdpPickImage(${i})" aria-label="Image ${i + 1} of ${media.length}"${i === activeIdx ? ' aria-current="true"' : ''}><img src="${m.thumb}" alt="" loading="lazy"></button>`
    }).join('')
  }

  window.bamV3PdpPickImage = function (idx) {
    const media = currentMedia()
    if (idx < 0 || idx >= media.length) return
    S.imageIndex = idx
    renderImage()
    renderThumbs()
  }

  // Populate the global #bam-v3-sticky-bar with the CURRENT product's state.
  // Simpler than the homepage sticky (no popover picker) — the on-page variant
  // picker already exists above, so the sticky is just a persistent buy shortcut.
  function renderPdpSticky() {
    const bar = document.getElementById('bam-v3-sticky-bar')
    if (!bar || !PDP) return
    // v is null when the colour isn't made in the chosen size: the bar still
    // follows the selection (it must never keep offering the previous variant).
    const v = currentVariant()
    const buyable = !!(v && v.available)
    const url = heroFor(S)?.thumb || PDP.defaultImage || ''
    const packLabel = (S.pack || '').replace(/pk$/, '-pack').replace('1-pack', 'Single')
    bar.innerHTML = `
      <div class="sb-left">
        <div class="sb-thumb-wrap">
          ${url ? `<img src="${url}" alt="${PDP.title}" class="sb-thumb">` : ''}
        </div>
        <div class="sb-info">
          <div class="sb-name">${PDP.title}</div>
          <div class="sb-chips">
            <button type="button" class="sb-chip" onclick="event.stopPropagation();bamV3PdpStickyOpen('color', event)" onmouseenter="event.stopPropagation();bamV3PdpStickyHover('color')" onmouseleave="event.stopPropagation();bamV3PdpStickyHoverEnd()">${stripFlow(S.color || 'Colour')}</button>
            <button type="button" class="sb-chip" onclick="event.stopPropagation();bamV3PdpStickyOpen('size', event)" onmouseenter="event.stopPropagation();bamV3PdpStickyHover('size')" onmouseleave="event.stopPropagation();bamV3PdpStickyHoverEnd()">${shortSize(S.size || 'Size')}</button>
            <button type="button" class="sb-chip" onclick="event.stopPropagation();bamV3PdpStickyOpen('pack', event)" onmouseenter="event.stopPropagation();bamV3PdpStickyHover('pack')" onmouseleave="event.stopPropagation();bamV3PdpStickyHoverEnd()">${packLabel || 'Pack'}</button>
          </div>
        </div>
      </div>
      <div class="sb-right">
        <div class="sb-price">${v ? fmtPrice(v.price) : ''}</div>
        <button type="button" class="sb-cta" onclick="bamV3PdpAddToCart()"${buyable ? '' : ' disabled'}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 002 1.58h9.78a2 2 0 001.95-1.57l1.65-7.43H5.12"/></svg>
          ${buyable ? 'Add to cart' : v ? 'Sold out' : `Not in ${shortSize(S.size)}`}
        </button>
      </div>
    `
  }

  // ── Sticky-bar variant popovers (colour / size / pack) — mirrors the shop sticky,
  //    but drives the PDP state so the sticky pill can change variant on mobile + desktop. ──
  let pdpHoverCloseTimer = null
  const PDP_HOVER_CLOSE_MS = 220
  function pdpScheduleHoverClose() {
    clearTimeout(pdpHoverCloseTimer)
    pdpHoverCloseTimer = setTimeout(() => { pdpHoverCloseTimer = null; pdpClosePopover() }, PDP_HOVER_CLOSE_MS)
  }
  function pdpCancelHoverClose() { clearTimeout(pdpHoverCloseTimer); pdpHoverCloseTimer = null }
  function pdpClosePopover() {
    pdpCancelHoverClose()
    document.querySelectorAll('#bam-v3-sticky-bar .sb-popover').forEach((el) => el.remove())
  }
  window.bamV3PdpStickyHover = function (axis) {
    pdpCancelHoverClose()
    const bar = document.getElementById('bam-v3-sticky-bar')
    if (!bar) return
    if (bar.querySelector(`.sb-popover[data-anchor="${axis}"]`)) return
    pdpStickyRender(axis)
  }
  window.bamV3PdpStickyHoverEnd = function () { pdpScheduleHoverClose() }
  window.bamV3PdpStickyOpen = function (axis, ev) {
    if (ev) ev.stopPropagation()
    const bar = document.getElementById('bam-v3-sticky-bar')
    if (!bar) return
    if (axis === null) { pdpClosePopover(); return }
    if (bar.querySelector(`.sb-popover[data-anchor="${axis}"]`)) { pdpClosePopover(); return }
    pdpStickyRender(axis)
  }
  function pdpStickyRender(axis) {
    const bar = document.getElementById('bam-v3-sticky-bar')
    if (!bar) return
    pdpClosePopover()
    const pop = document.createElement('div')
    pop.className = 'sb-popover'
    pop.setAttribute('data-anchor', axis)
    pop.addEventListener('mouseenter', pdpCancelHoverClose)
    pop.addEventListener('mouseleave', pdpScheduleHoverClose)
    let inner = ''
    if (axis === 'color') {
      const items = allValues('color').map(({ value, available }) => {
        const cls = 'sb-popover-swatch' + (S.color === value ? ' active' : '') + (available ? '' : ' oos')
        const val = value.replace(/'/g, "\\'")
        return `<button type="button" class="${cls}" style="background:${swatchBg(value)}" title="${stripFlow(value)}" onclick="bamV3PdpSelect('color','${val}');bamV3PdpStickyOpen(null)"></button>`
      }).join('')
      inner = `<div class="sb-popover-title">Colour</div><div class="sb-popover-swatches">${items}</div>`
    } else if (axis === 'size' || axis === 'pack') {
      const items = allValues(axis).map(({ value, available, others }) => {
        const label = axis === 'size' ? shortSize(value) : (value === '1pk' ? 'Single' : value.replace(/pk$/, '-pack'))
        // A size in stock in another colour stays tappable here too (it switches the colour).
        const oos = !available && !(others && others.length)
        const cls = 'sb-popover-opt' + (S[axis] === value ? ' active' : '') + (oos ? ' oos' : '')
        const val = value.replace(/'/g, "\\'")
        return `<button type="button" class="${cls}" onclick="bamV3PdpSelect('${axis}','${val}');bamV3PdpStickyOpen(null)">${label}</button>`
      }).join('')
      const title = axis === 'size' ? 'Size' : 'Pack'
      inner = `<div class="sb-popover-title">${title}</div><div class="sb-popover-grid">${items}</div>`
    } else { return }
    pop.innerHTML = inner
    bar.appendChild(pop)
    setTimeout(() => {
      document.addEventListener('click', function onDocClick(e) {
        if (!bar.contains(e.target)) { pdpClosePopover(); document.removeEventListener('click', onDocClick) }
      })
    }, 0)
  }

  function renderAll() {
    renderSwatches(); renderSizes(); renderPacks(); renderBuy(); renderImage(); renderThumbs(); renderPdpSticky()
  }

  window.bamV3PdpSelect = function (axis, value) {
    const { sel, note } = resolve(axis, value)
    S.color = sel.color; S.size = sel.size; S.pack = sel.pack
    S.note = note
    // Any variant change re-resolves the hero to the newly-selected variant's
    // image (color, size, or pack can each carry their own admin-assigned image),
    // dropping any manually-picked gallery thumbnail.
    S.imageIndex = null
    renderAll()
  }

  // ── Gallery arrows — cycle through the current-color-filtered media only ──
  function stepImage(delta) {
    const media = currentMedia()
    if (media.length <= 1) return
    if (S.imageIndex == null) {
      const currentUrl = document.getElementById('bam-v3-pdp-img')?.getAttribute('src')
      const idx = currentUrl ? media.findIndex((m) => m.big === currentUrl) : -1
      S.imageIndex = idx >= 0 ? idx : 0
    }
    S.imageIndex = (S.imageIndex + delta + media.length) % media.length
    renderImage()
    renderThumbs()
  }
  window.bamV3PdpPrevImage = function () { stepImage(-1) }
  window.bamV3PdpNextImage = function () { stepImage(1) }

  // ── ATC ──
  const ICON_CART = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 002 1.58h9.78a2 2 0 001.95-1.57l1.65-7.43H5.12"/></svg>'
  const ICON_CHECK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
  const SPINNER = '<span class="bam-v3-spin" aria-hidden="true"></span>'

  function setBtn(state) {
    const btn = document.getElementById('bam-v3-pdp-atc-btn'); if (!btn) return
    btn.classList.remove('added')
    btn.disabled = state === 'loading'
    if (state === 'loading') btn.innerHTML = `${SPINNER}<span>Adding…</span>`
    else if (state === 'success') { btn.classList.add('added'); btn.innerHTML = `${ICON_CHECK}<span>Added</span>` }
    else { btn.innerHTML = `${ICON_CART} Add to cart`; const v = currentVariant(); btn.disabled = !(v && v.available) }
  }

  window.bamV3PdpAddToCart = async function () {
    const v = currentVariant(); if (!v || !v.available) return
    setBtn('loading')
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] }),
      })
      if (!res.ok) throw new Error('add-to-cart failed')
      const item = await res.json().catch(() => null)
      setBtn('success')
      document.dispatchEvent(new CustomEvent('bam:cart:added', { detail: { variantId: v.id, item } }))
      setTimeout(() => setBtn('idle'), 1500)
    } catch (e) { console.error(e); setBtn('idle') }
  }

  // Size guide modal — opens on "size guide →" click. Close on backdrop / X / ESC.
  window.bamV3PdpOpenSizeGuide = function () {
    const m = document.getElementById('bam-v3-pdp-size-modal')
    if (!m) return
    m.setAttribute('data-open', '')
    m.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'
  }
  window.bamV3PdpCloseSizeGuide = function () {
    const m = document.getElementById('bam-v3-pdp-size-modal')
    if (!m) return
    m.removeAttribute('data-open')
    m.setAttribute('aria-hidden', 'true')
    document.body.style.overflow = ''
  }
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    const m = document.getElementById('bam-v3-pdp-size-modal')
    if (m?.hasAttribute('data-open')) bamV3PdpCloseSizeGuide()
  })

  // ── Init ──
  document.addEventListener('DOMContentLoaded', () => {
    PDP = loadData()
    if (!PDP) return
    selectDefaultAxes()
    renderAll()
  })
})();
